
import React, { createContext, useContext, useEffect, useState } from "react";
import { useApplyTheme } from "@/hooks/useApplyTheme";
import { ThemeConfig } from "@/types/theme";
import { supabase } from "@/integrations/supabase/client";
import { networkService } from "@/services/network.service";
import {
  getThemeConfig,
  setTheme,
  setThemeId as setThemeIdService,
  getThemeCSSVariables
} from "@/services/theme.service";
import { updateUserTheme } from "@/services/auth/profile.service";
import logger from "@/utils/logger";

export const DEFAULT_THEME: ThemeConfig = {
  backgroundColor: "#FFFFFF",
  textColor: "#000000",
  textPrimaryColor: "#ffffff",
  primaryColor: "#4caf50",
  borderColor: "#e2e8f0",
  cardBackgroundColor: "#FFFFFF",
  successColor: "#16a34a",
  errorColor: "#dc2626",
  warningColor: "#f59e0b",
  sectionTitleColor: "#1f2937",
  sectionTextColor: "#4b5563",
  buttonBorderRadius: "0.375rem",
  buttonPadding: "0.5rem 1rem",
  buttonFontWeight: "500",
  fontWeightNormal: "400",
  transitionSpeed: "0.2s",
};

interface ThemeConfigContextType {
  theme: ThemeConfig;
  refreshTheme: () => Promise<void>;
  setThemeId: (themeId: string) => Promise<void>;
}

const ThemeConfigContext = createContext<ThemeConfigContextType>({
  theme: DEFAULT_THEME,
  refreshTheme: async () => {},
  setThemeId: async () => {},
});

export const useThemeConfig = () => useContext(ThemeConfigContext);

export function ThemeConfigProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeConfig>(DEFAULT_THEME);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isDarkModeState, setIsDarkModeState] = useState(false);

  // Applica le variabili CSS del tema ogni volta che cambia
  useApplyTheme(theme);

  useEffect(() => {
    loadThemeConfig(); // inizializzazione
  }, []);


  const loadThemeConfig = async () => {
    try {
      const config = await getThemeConfig();
      const mergedConfig = { ...DEFAULT_THEME, ...config };
      setThemeState(mergedConfig);

      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user.id;

      let userThemeId: string | null = null;
      if (userId && networkService.isOnline()) {
        const { data, error } = await supabase
          .from("profiles")
          .select("theme_id")
          .eq("id", userId)
          .single();

        if (!error && data?.theme_id) {
          userThemeId = data.theme_id;
          await setThemeIdService(userThemeId);
        }
      }

      if (userThemeId) {
        const newConfig = await getThemeConfig(userThemeId);
        const merged = { ...DEFAULT_THEME, ...newConfig };
        setThemeState(merged);
      } else {
        // Il tema di default viene applicato tramite l'hook useApplyTheme
      }
      setIsInitialized(true);
    } catch (error) {
      console.error("Error loading theme config:", error);
      setIsInitialized(true); // Set initialized even if there's an error
    }
  };


  const refreshTheme = async () => {
    await loadThemeConfig();
  };

  const setIsDarkMode = async (isDark: boolean) => {
    // Update state immediately for UI responsiveness
    setIsDarkModeState(isDark);
    
    // Update backend in parallel
    await Promise.all([
      updateUserTheme(
        isDark ? '22222222-2222-2222-2222-222222222222' : '11111111-1111-1111-1111-111111111111'
      ),     // salva preferenza su Supabase
      setTheme(isDark)             // aggiorna theme ID in localStorage
    ]);
    
    // Load the updated theme configuration
    const config = await getThemeConfig();
    const merged = { ...DEFAULT_THEME, ...config };
    setThemeState(merged);
  };


  const setThemeId = async (themeId: string) => {
    // Salva l'ID tema in localStorage e sincronizza il profilo utente
    await setThemeIdService(themeId);
    await updateUserTheme(themeId);
    const config = await getThemeConfig(themeId);
    const merged = { ...DEFAULT_THEME, ...config };
    setThemeState(merged);
  };

  return (
    <ThemeConfigContext.Provider
      value={{
        theme,
        refreshTheme,
        setThemeId,
      }}
    >
      {children}
    </ThemeConfigContext.Provider>
  );
}
